import { Button } from '@/components/ui/Button'
import { Icon } from '@/components/ui/Icon'
import { Modal } from '@/components/ui/Modal'
import { formatDisplayDate, getDateString, parseDate } from '@/lib/date'
import moment from 'moment'
import { useState } from 'react'
import { DayPicker } from 'react-day-picker'
import 'react-day-picker/style.css'
import {
  useFormContext,
  type FieldValues,
  type Path,
  type RegisterOptions,
} from 'react-hook-form'
import styles from './DatePicker.module.scss'

interface DatePickerProps<T extends FieldValues> {
  name: Path<T>
  label?: string
  placeholder?: string
  required?: boolean
  disabled?: boolean
  readOnly?: boolean
  modalTitle?: string
  modalSubtitle?: string
  validation?: RegisterOptions<T>
  onChange?: (value: string) => void
  testId?: string
  minDate?: Date
}

export const DatePicker = <T extends FieldValues>({
  name,
  label,
  placeholder,
  required = false,
  disabled = false,
  readOnly = false,
  modalTitle,
  modalSubtitle,
  validation,
  onChange,
  testId,
  minDate,
}: DatePickerProps<T>) => {
  const [isOpen, setIsOpen] = useState(false)
  const [tempSelection, setTempSelection] = useState<Date | undefined>()
  const [displayMonth, setDisplayMonth] = useState<Date>(new Date())

  const {
    setValue,
    watch,
    formState: { errors },
    trigger,
  } = useFormContext<T>()

  const value = watch(name) as string
  const error = errors[name]
  const errorMessage = error?.message as string | undefined

  const containerClassNames = [styles.container, !label && styles.noLabel]
    .filter(Boolean)
    .join(' ')

  const triggerClassNames = [
    styles.trigger,
    error && styles.triggerError,
    readOnly && styles.triggerReadOnly,
  ]
    .filter(Boolean)
    .join(' ')

  const displayValue = value ? formatDisplayDate(value) : null

  const handleOpen = () => {
    if (disabled || readOnly) return
    const date = value ? parseDate(value) : moment().toDate()
    setTempSelection(date)
    setDisplayMonth(date)
    setIsOpen(true)
  }

  const handleClose = () => {
    setIsOpen(false)
  }

  const handleConfirm = () => {
    if (tempSelection) {
      const dateString = getDateString(tempSelection)
      setValue(name, dateString as T[typeof name], { shouldValidate: true })
      if (required || validation) {
        trigger(name)
      }
      onChange?.(dateString)
    }
    setIsOpen(false)
  }

  return (
    <div className={containerClassNames}>
      {label && (
        <label className={styles.label}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
      )}

      <button
        type="button"
        className={triggerClassNames}
        onClick={handleOpen}
        disabled={disabled}
        data-testid={testId || `datepicker-${name}`}
      >
        {displayValue ? (
          <span>{displayValue}</span>
        ) : (
          <span className={styles.placeholder}>
            {placeholder || 'Select date...'}
          </span>
        )}
        <Icon name="chevron-down" size={16} className={styles.chevron} />
      </button>

      {errorMessage && (
        <span
          className={styles.errorMessage}
          data-testid={testId ? `${testId}-error` : `datepicker-${name}-error`}
        >
          {errorMessage}
        </span>
      )}

      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={modalTitle || label || 'Select Date'}
        subtitle={modalSubtitle || 'Select a date to continue'}
        buttonLayout="column"
        buttons={[
          <Button
            key="confirm"
            text="Confirm Date"
            variant="primary"
            onClick={handleConfirm}
          />,
          <Button
            key="back"
            text="Go Back"
            variant="default"
            onClick={handleClose}
          />,
        ]}
      >
        <div
          className={styles.calendarWrapper}
          data-testid={
            testId ? `${testId}-calendar` : `datepicker-${name}-calendar`
          }
        >
          <DayPicker
            mode="single"
            month={displayMonth}
            onMonthChange={setDisplayMonth}
            selected={tempSelection}
            onSelect={setTempSelection}
            showOutsideDays={false}
            disabled={minDate ? { before: minDate } : undefined}
            classNames={{
              root: styles.calendar,
              months: styles.months,
              month: styles.month,
              month_caption: styles.monthCaption,
              caption_label: styles.captionLabel,
              nav: styles.nav,
              button_previous: styles.navButton,
              button_next: styles.navButton,
              weekdays: styles.weekdays,
              weekday: styles.weekday,
              weeks: styles.weeks,
              week: styles.week,
              day: styles.day,
              day_button: styles.dayButton,
              selected: styles.selected,
              today: styles.today,
              outside: styles.outside,
              chevron: styles.navChevron,
              disabled: styles.disabled,
            }}
          />
        </div>
      </Modal>
    </div>
  )
}
